import React, { useState } from 'react';

import { link } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { DialogActions, DialogContent, InputLabel, Typography } from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import {
  AlineButton,
  AlineDialog,
  AlineTooltip,
  AnimatedAlineDialog,
  Spacer,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { useCheckWorkflowAuthorization, useGetCurrentDoc } from 'src/hooks';
import { useCreateWorkflow, WorkflowBody } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

export const workflowStepsDialogOpenVar = makeVar<boolean>(false);

interface StyleProps {
  hasWorkflow: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { hasWorkflow }) => ({
  container: {
    maxWidth: 'unset',
    width: hasWorkflow ? theme.breakpoints.values.md : undefined,
  },
  workflowBody: {
    boxSizing: 'border-box',
    padding: theme.spacing(2),
  },
}));

export function WorkflowStepsDialog(): JSX.Element | null {
  const { document } = useGetCurrentDoc();
  const open = useReactiveVar(workflowStepsDialogOpenVar);
  const { classes } = useStyles({ hasWorkflow: Boolean(document?.workflow) });

  return (
    <AlineDialog
      classes={{ paper: classes.container }}
      headerChildren={<DialogHeader />}
      open={open}
      title="Workflow"
      onClose={() => workflowStepsDialogOpenVar(false)}
    >
      <WorkflowBody
        CannotViewWF={CannotViewWF}
        NoWorkflow={CreateWorkflowDialogBody}
        className={classes.workflowBody}
        workflowId={document?.workflow?.id ?? null}
      />
    </AlineDialog>
  );
}

const useCannotViewWFStyles = makeStyles()(_theme => ({
  body: {
    alignItems: 'center',
    display: 'flex',
    height: 240,
    justifyContent: 'center',
    width: '100%',
  },
}));

export function CannotViewWF(): JSX.Element {
  const { classes } = useCannotViewWFStyles();
  return (
    <DialogContent className={classes.body}>
      <Typography align="center" variant="subtitle1">
        Not authorized to view steps.
      </Typography>
    </DialogContent>
  );
}

interface CreateWorkflowDialogProps {
  onClose: () => void;
  open: boolean;
}

export function CreateWorkflowDialog({ onClose, open }: CreateWorkflowDialogProps): JSX.Element {
  return (
    <AnimatedAlineDialog open={open} title="Create workflow" onClose={onClose}>
      <CreateWorkflowDialogBody />
    </AnimatedAlineDialog>
  );
}

const useCreateWorkflowBodyStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: 480,
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
  },
}));

export function CreateWorkflowDialogBody(): JSX.Element {
  const { document } = useGetCurrentDoc();
  const [name, setName] = useState<string>('');
  const { classes } = useCreateWorkflowBodyStyles();
  const { createWorkflow, loading: creatingWorkflow } = useCreateWorkflow();
  const isPermittedToCreate = document?.requesterRole === Apollo.PermissionRole.Editor;
  const disabledReason = !isPermittedToCreate
    ? 'Only document editors can create a workflow'
    : !name
      ? 'Name your workflow'
      : '';

  return (
    <>
      <DialogContent className={classes.content}>
        <div className={classes.inputContainer}>
          <InputLabel className={classes.inputLabel} htmlFor="rowCount">
            Workflow name
          </InputLabel>
          <AlineInput
            placeholder="Name your workflow..."
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <AlineTooltip arrow placement="bottom" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={creatingWorkflow}
              variant="contained"
              onClick={async () => {
                if (!document) return;

                await createWorkflow({
                  variables: { input: { documentIds: [document.id], props: { name } } },
                  variant:
                    document.variant === Apollo.DocumentVariant.Template
                      ? Apollo.WorkflowVariant.Template
                      : Apollo.WorkflowVariant.Contract,
                });
              }}
            >
              Create Workflow
            </AlineButton>
          </div>
        </AlineTooltip>
      </DialogActions>
    </>
  );
}

function DialogHeader(): JSX.Element | null {
  const { document } = useGetCurrentDoc();
  const { canViewWorkflow } = useCheckWorkflowAuthorization(document?.workflow?.id ?? null);

  if (!document?.workflow || !canViewWorkflow) return null;

  return (
    <>
      <Spacer />
      <AlineButton
        color="primary"
        variant="contained"
        onClick={() => {
          if (!document?.workflow) return;

          openLink(link.WorkflowDetailTabSummary({ workflowId: document.workflow.id }));
        }}
      >
        View Workflow
      </AlineButton>
    </>
  );
}
